// v2 rabbit — a crouched, alert cottontail: big rounded haunches sitting back
// over a smaller pale chest, flat hind feet on the ground, little front paws, a
// lifted head with a pale muzzle and pink nose, tall two-layer ears (coat
// outside, pink blade inside) and the white scut tail. Three coat variants —
// sand, grey-brown and dark agouti — each with a paler underside. Ported 1:1
// from age-of-mcp's v2/rabbit.ts.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const COATS = [
  { coat: "#c2a474", belly: "#e2d2ac" },
  { coat: "#98917e", belly: "#c6beac" },
  { coat: "#7d6c52", belly: "#b5a88c" },
];

function rabbitParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = [
    // haunches — the big rear mass the rabbit sits back on
    { geometry: new THREE.IcosahedronGeometry(0.034, 0), color: c.coat,
      position: [-0.024, 0.034, 0], scale: [1.05, 1.0, 1.15] },
    // smaller chest tucked low in front, in the pale belly tone
    { geometry: new THREE.IcosahedronGeometry(0.025, 0), color: c.belly,
      position: [0.018, 0.028, 0], scale: [1.1, 1.0, 0.95] },
    // lifted, alert head
    { geometry: new THREE.IcosahedronGeometry(0.0215, 0), color: c.coat,
      position: [0.043, 0.069, 0], scale: [1.05, 1.0, 0.95] },
    // muzzle + pink nose
    { geometry: new THREE.IcosahedronGeometry(0.011, 0), color: c.belly,
      position: [0.06, 0.062, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.004, 0), color: "#d98a86",
      position: [0.069, 0.066, 0] },
    // eyes
    { geometry: new THREE.IcosahedronGeometry(0.0035, 0), color: "#241a12",
      position: [0.052, 0.077, 0.0145] },
    { geometry: new THREE.IcosahedronGeometry(0.0035, 0), color: "#241a12",
      position: [0.052, 0.077, -0.0145] },
    // tall ears — outer shells in the coat, pink inner blades in front
    { geometry: new THREE.BoxGeometry(0.013, 0.052, 0.008), color: c.coat,
      position: [0.033, 0.104, 0.0125], rotation: [-0.16, 0, -0.18] },
    { geometry: new THREE.BoxGeometry(0.013, 0.052, 0.008), color: c.coat,
      position: [0.033, 0.104, -0.0125], rotation: [0.16, 0, -0.18] },
    { geometry: new THREE.BoxGeometry(0.0065, 0.036, 0.004), color: "#e0a8a0",
      position: [0.0395, 0.103, 0.0125], rotation: [-0.16, 0, -0.18] },
    { geometry: new THREE.BoxGeometry(0.0065, 0.036, 0.004), color: "#e0a8a0",
      position: [0.0395, 0.103, -0.0125], rotation: [0.16, 0, -0.18] },
    // little front paws propping the chest
    { geometry: new THREE.CylinderGeometry(0.0055, 0.005, 0.03, 4), color: c.coat,
      position: [0.034, 0.015, 0.011] },
    { geometry: new THREE.CylinderGeometry(0.0055, 0.005, 0.03, 4), color: c.coat,
      position: [0.034, 0.015, -0.011] },
    // long hind feet flat on the ground
    { geometry: new THREE.BoxGeometry(0.036, 0.012, 0.013), color: c.coat,
      position: [-0.004, 0.006, 0.0235] },
    { geometry: new THREE.BoxGeometry(0.036, 0.012, 0.013), color: c.coat,
      position: [-0.004, 0.006, -0.0235] },
    // the white scut
    { geometry: new THREE.IcosahedronGeometry(0.0125, 0), color: "#f4efe2",
      position: [-0.052, 0.046, 0] },
  ];
  return parts;
}

export const rabbitV2 = defineModel({
  id: "rabbit-v2",
  batchId: "fable",
  name: "Rabbit (v2)",
  tags: ["creature","rabbit","low-poly","age-of-mcp","fable"],
  summary: "Low-poly rabbit.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.07,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => rabbitParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => rabbitParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => rabbitParts(2) },
  ],
  animations: [],
});
